// src/hooks/usePersonalMovies.ts
import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { createPersonalMoviesService, PersonalMovie, CreatePersonalMovieData, UpdatePersonalMovieData } from '../services/personalMoviesService'
import { sanitizePersonalMovies } from '../utils/personalMovieUtils'

interface CachedPersonalMovies {
  data: PersonalMovie[];
  timestamp: number;
  userId: string;
}

export const usePersonalMovies = () => {
  const { user, loading: authLoading } = useAuth()
  const [personalMovies, setPersonalMovies] = useState<PersonalMovie[]>([])
  const [loading, setLoading] = useState(false)
  const personalMoviesService = createPersonalMoviesService()

  // Cache expiration time: 5 minutes
  const CACHE_EXPIRY_MS = 5 * 60 * 1000;

  // Helper function to get cache key for specific user
  const getCacheKey = (userId: string) => `userPersonalMovies_${userId}`;

  // Helper function to clear all user personal movies caches
  const clearAllPersonalMoviesCaches = () => {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('userPersonalMovies_')) {
        localStorage.removeItem(key);
      }
    });
  };

  // Helper function to load personal movies from cache
  const loadFromCache = (userId: string): PersonalMovie[] | null => {
    try {
      const cacheKey = getCacheKey(userId);
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const cachedData: CachedPersonalMovies = JSON.parse(cached);
      
      // Validate cache: check if it's for the right user and not expired
      if (cachedData.userId !== userId) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      const now = Date.now();
      if (now - cachedData.timestamp > CACHE_EXPIRY_MS) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      // Sanitize cached data as well, in case it was corrupted
      const sanitizedData = sanitizePersonalMovies(cachedData.data);
      return sanitizedData;
    } catch (error) {
      console.warn('Failed to load personal movies from cache:', error);
      return null;
    }
  };

  // Helper function to save personal movies to cache
  const saveToCache = (userId: string, moviesData: PersonalMovie[]) => {
    try {
      const cacheKey = getCacheKey(userId);
      const cacheData: CachedPersonalMovies = {
        data: moviesData,
        timestamp: Date.now(),
        userId: userId
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to save personal movies to cache:', error);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      if (user) {
        // User is logged in - try to load cached personal movies first
        const cachedPersonalMovies = loadFromCache(user.id);
        if (cachedPersonalMovies) {
          setPersonalMovies(cachedPersonalMovies);
        }
        // Always load fresh data in the background
        loadPersonalMovies();
      } else {
        // User is logged out - clear personal movies and all caches
        setPersonalMovies([]);
        clearAllPersonalMoviesCaches();
      }
    }
  }, [user, authLoading])

  const loadPersonalMovies = async () => {
    if (!user) return
    
    setLoading(true)
    try {
      const userPersonalMovies = await personalMoviesService.getUserPersonalMovies(user.id)
      const validMovies = sanitizePersonalMovies(userPersonalMovies);
      
      setPersonalMovies(validMovies)
      saveToCache(user.id, validMovies)
    } catch (error) {
      console.error('Failed to load personal movies:', error)
      setPersonalMovies([])
    } finally {
      setLoading(false)
    }
  }

  const createPersonalMovie = async (data: CreatePersonalMovieData) => {
    if (!user) return

    try {
      const newMovie = await personalMoviesService.createPersonalMovie(user.id, data)
      
      if (!newMovie || !newMovie.id) {
        throw new Error('Failed to create personal movie: invalid response');
      }
      
      const updatedMovies = [newMovie, ...personalMovies.filter(movie => movie && movie.id)]
      setPersonalMovies(updatedMovies)
      saveToCache(user.id, updatedMovies)
      return newMovie
    } catch (error) {
      console.error('Failed to create personal movie:', error)
      throw error
    }
  }

  const updatePersonalMovie = async (id: string, data: UpdatePersonalMovieData) => {
    if (!user) return

    try {
      const updatedMovie = await personalMoviesService.updatePersonalMovie(user.id, id, data)
      
      // More flexible validation - just check if we have an id
      if (!updatedMovie || !updatedMovie.id) {
        console.warn('Update succeeded but received invalid movie data:', updatedMovie);
        // If the service couldn't return proper data, we'll optimistically update the local state
        // with the data we sent, merged with the existing movie data
        const existingMovie = personalMovies.find(movie => movie && movie.id === id);
        if (existingMovie) {
          const optimisticUpdate = {
            ...existingMovie,
            ...data,
            updated_at: new Date().toISOString()
          };
          
          const updatedMovies = personalMovies
            .filter(movie => movie && movie.id)
            .map(movie => movie.id === id ? optimisticUpdate : movie);
          
          setPersonalMovies(updatedMovies);
          saveToCache(user.id, updatedMovies);
          return optimisticUpdate;
        } else {
          throw new Error('Failed to update personal movie: movie not found locally');
        }
      }
      
      const updatedMovies = personalMovies
        .filter(movie => movie && movie.id)
        .map(movie =>
          movie.id === id ? updatedMovie : movie
        )
      setPersonalMovies(updatedMovies)
      saveToCache(user.id, updatedMovies)
      return updatedMovie
    } catch (error) {
      console.error('Failed to update personal movie:', error)
      // If the update completely failed, we should refresh the data to ensure consistency
      loadPersonalMovies();
      throw error
    }
  }

  const deletePersonalMovie = async (id: string) => {
    if (!user) return

    try {
      await personalMoviesService.deletePersonalMovie(user.id, id)
      const updatedMovies = personalMovies.filter(movie => movie && movie.id && movie.id !== id)
      setPersonalMovies(updatedMovies)
      saveToCache(user.id, updatedMovies)
    } catch (error) {
      console.error('Failed to delete personal movie:', error)
      throw error
    }
  }

  return {
    personalMovies,
    loading,
    createPersonalMovie,
    updatePersonalMovie,
    deletePersonalMovie,
    refreshPersonalMovies: loadPersonalMovies,
    clearCache: () => {
      if (user) {
        const cacheKey = getCacheKey(user.id);
        localStorage.removeItem(cacheKey);
      }
    }
  }
}